type RemoteUserName = { platform: string; username: string; displayName?: string | null };

const platformPriority = ['twitch', 'youtube', 'kick'];

function getRemoteName(remoteUsers: Record<string, RemoteUserName>): string | null {
  const platform = platformPriority.find((p) => remoteUsers[p]);
  const remoteUser = platform ? remoteUsers[platform] : Object.values(remoteUsers)[0];
  if (!remoteUser) {
    return null;
  }
  return remoteUser.displayName || remoteUser.username || null;
}

/**
 * Legacy casing rescue for an account that has a username but no display name
 * yet: borrow the capitalization from a linked platform account with the same
 * name. Superseded by user.displayName wherever that is set.
 */
function getCasedDisplayName(
  username: string,
  remoteUsers: Record<string, RemoteUserName>,
): string | null {
  const ordered = [
    ...platformPriority.map((p) => remoteUsers[p]),
    ...Object.entries(remoteUsers)
      .filter(([platform]) => !platformPriority.includes(platform))
      .map(([, remoteUser]) => remoteUser),
  ];
  const match = ordered.find(
    (remoteUser) => remoteUser?.displayName?.toLowerCase() === username.toLowerCase(),
  );
  return match?.displayName ?? null;
}

export function getDisplayName({
  username,
  displayName,
  fallbackName,
  remoteUsers,
}: {
  username?: string | null;
  displayName?: string | null;
  /**
   * Last resort for an account with no name anywhere: the local part of the
   * Kratos email, already split on the server so the address never reaches the
   * client. See `.server/root-identity.ts`.
   */
  fallbackName?: string;
  remoteUsers?: Record<string, RemoteUserName>;
}): string {
  if (username) {
    // The songlist display name is the capitalization (or, on a non-latin
    // account, the localized form) the user chose for this very slug, so it
    // outranks any name borrowed from a linked platform.
    if (displayName) {
      return displayName;
    }
    const casedName = remoteUsers ? getCasedDisplayName(username, remoteUsers) : null;
    return casedName ?? username;
  }
  const remoteName = remoteUsers ? getRemoteName(remoteUsers) : null;
  if (remoteName) {
    return remoteName;
  }
  return fallbackName ?? '';
}
